// Ao soltar em "Próximos dias" ou "Para depois": "Informe uma data ou deixe vazio" (AC-182).
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isValidDateKey, normalizeTime } from '../domain/dates';
import type { SectionKey } from '../domain/ordering';
import type { Task } from '../domain/task';
import { Dialog } from './EditTaskDialog';
import { Icon } from './Icon';
import { TimeInput } from './TimeInput';
import type { TimeFormat } from '../domain/settings';

interface Props {
  task: Task;
  section: SectionKey;
  onConfirm(dueDate: string | null, dueTime: string | null): void;
  onCancel(): void;
  timeFormat?: TimeFormat;
}

export function DropDateDialog({ task, section, onConfirm, onCancel, timeFormat = '24h' }: Props) {
  const { t } = useTranslation();
  const [date, setDate] = useState(task.due_date ?? '');
  const [time, setTime] = useState(task.due_time ?? '');
  const [error, setError] = useState(false);

  const confirm = () => {
    if (date && !isValidDateKey(date)) {
      setError(true);
      return;
    }
    onConfirm(date || null, date && time ? normalizeTime(time) : null);
  };

  return (
    <Dialog labelId="drop-date-title" onClose={onCancel}>
      <div className="drop-date" data-testid="drop-date">
        <h2 id="drop-date-title" className="dialog__title">
          {t('drag.askTitle')}
        </h2>
        <p className="dialog__body">{t('drag.askSub', { title: task.title, section: t(`sections.${section}`) })}</p>
        <div className="drop-date__fields">
          <label className="drop-date__field">
            <span>{t('newTask.date')}</span>
            <input
              className="input"
              type="date"
              aria-label={t('newTask.date')}
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                setError(false);
              }}
            />
          </label>
          {date && (
            <label className="drop-date__field">
              <span>{t('newTask.time')}</span>
              <TimeInput className="input" label={t('newTask.time')} value={time} format={timeFormat} onChange={setTime} />
            </label>
          )}
        </div>
        {error && <p className="field-error">{t('errors.task.invalid_date')}</p>}
        <p className="drop-date__hint">
          <Icon name="calendar" size={13} />
          {date ? t('drag.dateHint') : t('drag.emptyHint')}
        </p>
        <div className="dialog__actions">
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            {t('common.cancel')}
          </button>
          <button type="button" className="btn btn--primary" onClick={confirm}>
            <Icon name="check" size={14} />
            {t('drag.move')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
